import React from 'react'
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  Chip,
} from '@mui/material'
import { ExpandMore, ExpandLess, Article, Loop, Code, Segment } from '@mui/icons-material'
import { SelectedItem } from './InspectorLayout'

// Tree lines component to show hierarchical connections
const TreeLines = ({
  level,
  isLastChild,
  hasChildren,
}: {
  level: number
  isLastChild: boolean
  hasChildren: boolean
}) => {
  if (level === 0) return null

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {/* Vertical line from parent */}
      <Box
        sx={{
          position: 'absolute',
          left: `${(level - 1) * 16 + 8}px`,
          top: 0,
          width: '1px',
          height: '50%',
          backgroundColor: 'divider',
        }}
      />

      {/* Horizontal line to current item */}
      <Box
        sx={{
          position: 'absolute',
          left: `${(level - 1) * 16 + 8}px`,
          top: '50%',
          width: '8px',
          height: '1px',
          backgroundColor: 'divider',
        }}
      />

      {/* Continue vertical line if not last child */}
      {!isLastChild && (
        <Box
          sx={{
            position: 'absolute',
            left: `${(level - 1) * 16 + 8}px`,
            top: '50%',
            width: '1px',
            height: '50%',
            backgroundColor: 'divider',
          }}
        />
      )}
    </Box>
  )
}

// Centralized icon function that can be reused
export const getIcon = (type: string) => {
  switch (type) {
    case 'main-segment':
      return <Segment fontSize="small" />
    case 'loop':
      return <Loop fontSize="small" />
    case 'segment':
      return <Segment fontSize="small" />
    case 'element':
      return <Code fontSize="small" />
    default:
      return <Article fontSize="small" />
  }
}

export interface NavigationItemProps {
  item: any
  level: number
  selectedItem: SelectedItem | null
  expandedItems: Set<string>
  onSelect: (item: any) => void
  onExpand: (itemId: string) => void
  onCollapse: (itemId: string) => void
  readonly?: boolean
  isLastChild?: boolean
  searchTerm?: string
}

const NavigationItem = React.memo<NavigationItemProps>(
  ({
    item,
    level,
    selectedItem,
    expandedItems,
    onSelect,
    onExpand,
    onCollapse,
    readonly = false,
    isLastChild = false,
    searchTerm = '',
  }) => {
    const isExpanded = expandedItems.has(item.id)
    const isSelected = selectedItem?.id === item.id
    const hasChildren = item.children && item.children.length > 0

    const handleClick = () => {
      onSelect(item)
      // Auto-expand if item has children and is not already expanded, or collapse if already expanded
      if (hasChildren) {
        if (isExpanded) {
          onCollapse(item.id)
        } else {
          onExpand(item.id)
        }
      }
    }

    const handleExpandClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isExpanded) {
        onCollapse(item.id)
      } else {
        onExpand(item.id)
      }
    }

    const getItemCount = () => {
      if (!hasChildren) return null

      switch (item.type) {
        case 'main-segment':
          if (item.id === 'loops') {
            return item.data.loops?.length || 0
          }
          // For other main segments, show element count
          return item.data.elements?.length || 0
        case 'loop':
          return item.data.segments?.length || 0
        case 'segment':
          return item.data.elements?.length || 0
        default:
          return null
      }
    }

    const itemCount = getItemCount()

    return (
      <>
        <ListItem
          disablePadding
          sx={{
            pl: level * 1.5,
            position: 'relative',
          }}
        >
          <TreeLines level={level} isLastChild={isLastChild} hasChildren={hasChildren} />
          <ListItemButton
            selected={isSelected}
            onClick={handleClick}
            sx={{
              minHeight: '32px',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <ListItemIcon sx={{ minWidth: '32px' }}>{getIcon(item.type)}</ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" noWrap>
                    {item.title}
                  </Typography>
                  {itemCount !== null && itemCount > 0 && (
                    <Chip
                      label={
                        item.type === 'main-segment' && item.id !== 'loops'
                          ? `${itemCount} elem`
                          : itemCount
                      }
                      size="small"
                      variant="filled"
                      sx={{ height: '18px', fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              }
            />
            {hasChildren && (
              <Box onClick={handleExpandClick} sx={{ cursor: 'pointer' }}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </Box>
            )}
          </ListItemButton>
        </ListItem>

        {/* Render children */}
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box>
              {item.children.map((child: any, index: number) => (
                <NavigationItem
                  key={child.id}
                  item={child}
                  level={level + 1}
                  selectedItem={selectedItem}
                  expandedItems={expandedItems}
                  onSelect={onSelect}
                  onExpand={onExpand}
                  onCollapse={onCollapse}
                  readonly={readonly}
                  isLastChild={index === item.children.length - 1}
                  searchTerm={searchTerm}
                />
              ))}
            </Box>
          </Collapse>
        )}
      </>
    )
  }
)

NavigationItem.displayName = 'NavigationItem'

export default NavigationItem
